import { memo, useCallback, useMemo, useRef, Fragment, ReactElement } from 'react';
import { EditOutlined, EyeOutlined, FormOutlined, ProfileOutlined, PlusOutlined, TeamOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Flex, Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import { Table, TableComponent, TableProps } from '@ohpen/enso-ui';
import { ActionEntityType, FieldType, FirmGetAllResponse } from '@pmp/api';
import { Guard, useGuard } from '@ohpen/react-authz';

import { columns } from '../tableConfig';
import { OPERATION_ORDER } from '../utils';
import { FirmsListProps } from '../interfaces';
import { useListTableState } from '@pmp/hooks';
import { formatDate, lockActionTitle } from '@pmp/utils';

const entityType = ActionEntityType.LENDER_FIRMS;
const rowKey = 'ohpenId';
// Every firm-scoped BFF route takes `id`, so that is the key the assistant reports, not the table's.
const assistantRowKey = 'id';

export const FirmsList = memo(
  ({
    isFetching,
    data,
    onCreate,
    onView,
    onEdit,
    onDelete,
    onOpenInPanel,
    onSeeFirmCases,
    onManageFirmCases,
    onSeeFirmIntermediaries,
    valueLists,
  }: FirmsListProps<FirmGetAllResponse>): ReactElement => {
    const tableRef = useRef<TableComponent | null>(null);
    const { t } = useTranslation();
    const { allowed: showRowSelection } = useGuard({ flags: ['enableExports'] });
    const tableState = useListTableState<FirmGetAllResponse>(entityType, assistantRowKey, tableRef, columns);
    const { allowed: canManageCases } = useGuard({ permissions: ['brokerPortalCases:update'] });
    const { allowed: canViewCases } = useGuard({ permissions: ['brokerPortalCases:view'] });

    const onAdd = useCallback(() => {
      onCreate();
    }, [onCreate]);

    const getRowActions: (record: FirmGetAllResponse) => ReactElement[] = useCallback(
      record => {
        const rowActions = [
          <Tooltip key="view" title={t('global.buttons.view')} placement="top">
            <Button type="text" aria-label="View firms" icon={<EyeOutlined />} onClick={() => onView(record)} />
          </Tooltip>,
          <Guard key="edit" permissions={['firms:update']} flags={['enableFirmsCrud']}>
            <Tooltip key="edit" title={lockActionTitle('edit', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Edit firms"
                icon={<EditOutlined key="edit" />}
                onClick={() => onEdit(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
          // TODO PMP-1813 restore once the active-cases check for firms is implemented
          // <Guard key="delete" permissions={['firms:delete']} flags={['enableFirmsCrud']}>
          //   <Tooltip key="delete" title={lockActionTitle('delete', record.lockedByActionId)} placement="top">
          //     <Button
          //       type="text"
          //       aria-label="Delete firms"
          //       icon={<DeleteOutlined key="delete" rev={undefined} />}
          //       onClick={() => onDelete(record)}
          //       disabled={!!record.lockedByActionId}
          //     />
          //   </Tooltip>
          // </Guard>,
          <Tooltip key="panel" title={t('lender.firms.labels.openInPanel')} placement="top">
            <Button type="text" aria-label="Open in Panel" icon={<FormOutlined key="edit" />} onClick={() => onOpenInPanel(record)} />
          </Tooltip>,
          canManageCases ? (
            <Tooltip key="cases" title={t('lender.firms.labels.manageFirmCases')} placement="top">
              <Button
                type="text"
                aria-label="Manage Firm Cases"
                icon={<ProfileOutlined key="edit" />}
                onClick={() => onManageFirmCases(record)}
              />
            </Tooltip>
          ) : canViewCases ? (
            <Tooltip key="cases" title={t('lender.firms.labels.seeFirmCases')} placement="top">
              <Button
                type="text"
                aria-label="See Firm Cases"
                icon={<ProfileOutlined key="edit" />}
                onClick={() => onSeeFirmCases(record)}
              />
            </Tooltip>
          ) : (
            <Fragment key="cases" />
          ),
          <Guard key="seeFirmIntermediaries" permissions={['brokers:view']} flags={['enableBrokers']}>
            <Tooltip key="seeFirmIntermediaries" title={t('lender.firms.labels.seeFirmIntermediaries')} placement="top">
              <Button
                type="text"
                aria-label="See firms' intermediaries"
                icon={<TeamOutlined key="seeFirmIntermediaries" />}
                onClick={() => onSeeFirmIntermediaries(record)}
              />
            </Tooltip>
          </Guard>,
        ];
        return rowActions;
      },
      [t, onView, onEdit, onOpenInPanel, onSeeFirmCases, onManageFirmCases, onSeeFirmIntermediaries, onDelete, canManageCases, canViewCases],
    );

    const processedData = useMemo((): FirmGetAllResponse[] => {
      if (!data) return [];
      const principals = valueLists?.[FieldType.PRINCIPALS];
      const nameToId: Record<string, string> = principals
        ? Object.fromEntries(Object.entries(principals).map(([id, name]) => [name, id]))
        : {};
      return data.map(firm => ({
        ...firm,
        operationsWhitelist: [...firm.operationsWhitelist].sort(
          (a, b) => OPERATION_ORDER.indexOf(a.key ?? '') - OPERATION_ORDER.indexOf(b.key ?? ''),
        ),
        principalFirm: principals
          ? firm.principalFirm.map(ref => ({
              key: nameToId[ref.name ?? ''] ?? '',
              value: ref.name ?? '',
            }))
          : firm.principalFirm,
      })) as unknown as FirmGetAllResponse[];
    }, [data, valueLists]);

    const tableProps: TableProps<ActionEntityType, FirmGetAllResponse> = useMemo(
      () => ({
        ...tableState,
        id: entityType,
        ref: tableRef,
        dataSource: processedData,
        columns,
        rowKey,
        pagination: true,
        scroll: { x: 'max-content', y: '100%' },
        loading: isFetching,
        showRowSelection,
        getRowActions,
        showViews: true,
        valueLists,
      }),
      [tableState, getRowActions, valueLists, processedData, isFetching, showRowSelection],
    );

    return (
      <Table {...tableProps}>
        <Table.ActionsBar>
          <Flex justify="space-between">
            <Space size="middle">
              <Table.GlobalSearch />
            </Space>
            <Space size="middle">
              <Table.ResetFiltersButton />
              <Table.SelectColumnsButton />
              <Guard flags={['enableExports']}>
                <Table.ExportButton
                  editableFileName
                  fileName={`Firms ${formatDate()}`}
                  sheetName="Firms"
                  disabled={isFetching}
                  exportView
                />
              </Guard>
              <Guard permissions={['firms:create']} flags={['enableFirmsCrud']}>
                <Button icon={<PlusOutlined />} onClick={onAdd} type="primary">
                  {t('global.labels.create')}
                </Button>
              </Guard>
            </Space>
          </Flex>
        </Table.ActionsBar>
        <Table.BulkBar xItemsSelectedLabel="lender.firms.labels.xFirmsSelected">
          <Space size="middle">
            <Guard flags={['enableExports']}>
              <Table.ExportButton
                editableFileName
                fileName={`Firms ${formatDate()}`}
                sheetName="Firms"
                disabled={isFetching}
                exportView
              />
            </Guard>
          </Space>
        </Table.BulkBar>
      </Table>
    );
  },
);
